import { useState } from "react";
import { HeartHandshake, BookMarked, Building2, HandHeart, ArrowUpRight, Mail } from "lucide-react";
import { toast } from "sonner";
import { Reveal } from "@/components/Reveal";
import { GiveButter } from "@/components/GiveButter";
import { api, LINKS } from "@/lib/api";

const WAYS = [
  { icon: HandHeart, title: "Volunteer", body: "Give your time at book drives, festivals, and community events across Greater Boston." },
  { icon: HeartHandshake, title: "Sponsor an Event", body: "Underwrite a block party, panel, or gala and put your name behind lasting impact." },
  { icon: BookMarked, title: "Donate Books", body: "Add titles from our wishlist to put more books directly into children's hands." },
  { icon: Building2, title: "Corporate Partner", body: "Build a partnership that aligns your mission with literacy, culture, and youth." },
];

const REASONS = ["Volunteer", "Sponsor an Event", "Donate Books", "Corporate Partnership"];

export default function GetInvolved() {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [interest, setInterest] = useState(REASONS[0]);
  const [busy, setBusy] = useState(false);

  const subscribe = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/newsletter", { email, name, interest });
      toast.success("Thanks for reaching out — we'll be in touch soon!");
      setEmail(""); setName(""); setInterest(REASONS[0]);
    } catch (err) {
      toast.error(err?.response?.data?.detail?.[0]?.msg || "Please enter a valid email");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="get-involved-page">
      <section className="bg-ink crosshatch radial-glow pt-[68px]" data-testid="gi-hero">
        <div className="max-w-[1240px] mx-auto px-6 lg:px-10 py-20 lg:py-28 relative z-10">
          <span className="eyebrow">Get Involved</span>
          <h1 className="font-serif-eth font-light text-white mt-6 text-5xl lg:text-[72px] leading-[1.02] max-w-4xl">
            There's a place for you <em>in this community.</em>
          </h1>
          <p className="font-body-eth text-white/60 text-lg mt-7 max-w-2xl leading-relaxed">
            Volunteer, sponsor, donate books, or partner with us. Every contribution helps build a more literate, connected, and joyful Boston.
          </p>
        </div>
      </section>

      {/* Newsletter / contact — moved to top */}
      <section className="bg-sand" data-testid="newsletter-section">
        <div className="max-w-[820px] mx-auto px-6 lg:px-10 py-16 lg:py-20 text-center">
          <Reveal><span className="eyebrow dark mx-auto">Stay Connected</span></Reveal>
          <Reveal delay={80}>
            <h2 className="font-serif-eth font-light text-ink text-4xl lg:text-5xl mt-5">
              Never miss a <em>moment.</em>
            </h2>
          </Reveal>
          <Reveal delay={140}>
            <p className="font-body-eth text-mute mt-4">Get event invites, impact updates, and stories from the community in your inbox.</p>
          </Reveal>
          <Reveal delay={200}>
            <form onSubmit={subscribe} className="mt-10 text-left max-w-2xl mx-auto" data-testid="newsletter-form">
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="font-body-eth text-[11px] uppercase tracking-wider text-mute">Name</label>
                  <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name" data-testid="newsletter-name" className="eth-input w-full py-2.5 text-ink mt-1" />
                </div>
                <div>
                  <label className="font-body-eth text-[11px] uppercase tracking-wider text-mute">Email</label>
                  <input required type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@email.com" data-testid="newsletter-email" className="eth-input w-full py-2.5 text-ink mt-1" />
                </div>
              </div>
              <div className="grid sm:grid-cols-[1fr_auto] gap-4 items-end mt-4">
                <div>
                  <label className="font-body-eth text-[11px] uppercase tracking-wider text-mute">I'm reaching out about</label>
                  <select value={interest} onChange={(e) => setInterest(e.target.value)} data-testid="newsletter-interest" className="eth-input w-full py-2.5 text-ink mt-1 bg-transparent">
                    {REASONS.map((r) => <option key={r} value={r}>{r}</option>)}
                  </select>
                </div>
                <button type="submit" disabled={busy} data-testid="newsletter-submit" className="btn-teal font-body-eth text-sm font-semibold px-8 py-3 inline-flex items-center justify-center gap-2 disabled:opacity-60">
                  <Mail size={15} /> {busy ? "..." : "Get in Touch"}
                </button>
              </div>
            </form>
          </Reveal>
        </div>
      </section>

      {/* Ways to help */}
      <section className="bg-cream">
        <div className="max-w-[1240px] mx-auto px-6 lg:px-10 py-20 lg:py-24">
          <Reveal><span className="eyebrow dark">Ways To Help</span></Reveal>
          <Reveal delay={80}>
            <h2 className="font-serif-eth font-light text-ink text-4xl lg:text-5xl mt-5">Pick your <em>path.</em></h2>
          </Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mt-12">
            {WAYS.map((w, i) => (
              <Reveal key={w.title} delay={i * 100}>
                <div className="group eth-card bg-white border border-[var(--line)] p-7 h-full">
                  <w.icon size={26} className="text-teal" />
                  <h3 className="font-serif-eth text-2xl text-ink mt-5 underline-grow inline-block pb-1">{w.title}</h3>
                  <p className="font-body-eth text-mute text-sm leading-relaxed mt-3">{w.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Donate + wishlist */}
      <section className="bg-sand">
        <div className="max-w-[1240px] mx-auto px-6 lg:px-10 py-20 lg:py-24 grid lg:grid-cols-2 gap-10 items-start">
          <Reveal>
            <div className="bg-white border border-[var(--line)] p-8" style={{ borderRadius: 2 }}>
              <span className="eyebrow dark">Make a Gift</span>
              <h2 className="font-serif-eth font-light text-ink text-3xl lg:text-4xl mt-4">Donate <em>directly.</em></h2>
              <p className="font-body-eth text-mute text-sm mt-3 leading-relaxed">
                Your gift funds books, events, and youth programs. Give securely through GiveButter below.
              </p>
              <div className="mt-6 min-h-[120px]">
                <GiveButter widgetId="Ly87xj" />
              </div>
              <a href={LINKS.donate} target="_blank" rel="noreferrer" data-testid="gi-donate-link" className="btn-gold w-full text-center font-body-eth text-sm font-semibold py-3.5 mt-4 inline-flex items-center justify-center gap-2">
                Donate on GiveButter <ArrowUpRight size={16} />
              </a>
            </div>
          </Reveal>

          <Reveal delay={120}>
            <div className="bg-ink crosshatch p-8 text-white" style={{ borderRadius: 2 }}>
              <span className="eyebrow">Book Wishlist</span>
              <h2 className="font-serif-eth font-light text-3xl lg:text-4xl mt-4">Gift a <em>book.</em></h2>
              <p className="font-body-eth text-white/60 text-sm mt-3 leading-relaxed">
                Every book on our Bookshop.org wishlist goes straight to a child at a Pages of Progress event. Pick a title and change a reader's life.
              </p>
              <div className="mt-8 flex items-start gap-4 border-t border-white/10 pt-6">
                <BookMarked size={26} className="text-glow shrink-0 mt-1" />
                <div>
                  <div className="font-serif-eth text-xl">10,000+ books distributed</div>
                  <div className="font-body-eth text-white/50 text-sm">and counting, thanks to donors like you.</div>
                </div>
              </div>
              <a href={LINKS.wishlist} target="_blank" rel="noreferrer" data-testid="gi-wishlist-link" className="btn-teal w-full text-center font-body-eth text-sm font-semibold py-3.5 mt-8 inline-flex items-center justify-center gap-2">
                View the Wishlist <ArrowUpRight size={16} />
              </a>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Newsletter */}
    </div>
  );
}
